import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, generatePath } from "react-router-dom";
import { observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import I14Star from "assets/icons/I14Star";
import TodayStore from "stores/TodayStore";
import WorkoutStore from "stores/WorkoutStore";
import CoachStore from "stores/CoachStore";

import GridCollection from "components/GridCollection";
import { Page } from "components/Layout";
import { Placeholders } from "assets/images/placeholders";
import Placeholder from "components/Placeholder";
import NavList from "./NavList/NavList";
import { NoInternetConnectionError } from "services/api";
import ROUTES from "navigation/routes";
import styleConst from "constants/style";
import { STRUCTURE_TYPE } from "constants/common";
import { Collection } from "components/Collection";
import { COLLECTION_TYPE } from "constants/collection";
import {
  Background,
  FavoriteButton,
  Wrapper,
  Title,
  TitleContainer,
  SectionTitle,
  SectionHeader,
  ViewAllButton,
  CollectionWrapper,
} from "./styles";
import I24ArrowRightSmall from "assets/icons/I24ArrowRightSmall";

type Props = {} & WithTranslation;

const WorkoutMain: React.FC<Props> = observer(props => {
  const t: (key: string) => string = props.t;
  const navigate = useNavigate();
  const location = useLocation();

  const [page, setPage] = useState<number>(1);
  const [isNoInternetConnectionError, setNoInternetConnectionError] =
    useState<boolean>(false);

  const context = WorkoutStore.context;
  const needMore = Boolean(context && page < context.pages);

  const fetchFamilies = useCallback(async () => {
    try {
      await WorkoutStore.fetchFamilies();
    } catch (error: any) {
      if (error instanceof NoInternetConnectionError) {
        setNoInternetConnectionError(true);
      }
    }
  }, []);

  useEffect((): void => {
    (async () => {
      await fetchFamilies();
      TodayStore.fetchTodayData();
      CoachStore.syncCoachConnect();
    })();
  }, [location.pathname, fetchFamilies]);

  const onLoadMore = (): void => {
    if (needMore) {
      setPage(prev => prev + 1);
      fetchFamilies();
    }
  };

  const onNavigateToFavorites = (): void => {
    navigate(generatePath(ROUTES.WORKOUT_FAVORITES));
  };

  const onNavigateToWeeklyCategory = (item: any): void => {
    const { id, title } = item;
    if (id) {
      navigate(
        generatePath(ROUTES.WORKOUT_CATEGORY_DETAILS, { categoryId: id }),
        { state: { id, title } },
      );
    }
  };

  const onNavigateToCategory = (item: any, familyId: number, familyTitle: string = ""): void => {
    const { id, title } = item;
    if (id) {
      navigate(
        generatePath(ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS, {
          categoryId: id,
          workoutGroup: "all",
        }),
        { state: { id, title, familyId, familyTitle } },
      );
    }
  };

  const onViewAllFamily = (family: any): void => {
    navigate(
      generatePath(ROUTES.WORKOUT_FAMILY_DETAILS, { familyId: family.id }),
      { state: { familyTitle: family.title } },
    );
  };

  const families = WorkoutStore.families.slice();
  const weeklyFamily = families.find(
    f => f.structureType === STRUCTURE_TYPE.WEEK,
  );

  const weeklyItems = weeklyFamily
    ? weeklyFamily.workoutsCategories.slice()
    : [];

  const otherFamilies = weeklyFamily
    ? families.filter(f => f.id !== weeklyFamily.id)
    : families;

  return (
    <Page
      isLoading={WorkoutStore.isLoading}
      isNoInternetConnectionError={isNoInternetConnectionError}
      title=" "
      contentBackground={<Background />}
      topNavigation={<NavList />}
    >
      <Wrapper>
        <TitleContainer>
          <Title>{t("titles.all-workouts")}</Title>
          <FavoriteButton
            iconLeft={<I14Star />}
            text={t("actions.go-to-favorites")}
            onClick={onNavigateToFavorites}
            height={48}
            fontSize={18}
            fontWeight={600}
            fontFamily={styleConst.fonts.montserrat}
          />
        </TitleContainer>

        {weeklyItems.length > 0 && (
          <CollectionWrapper>
            <Collection
              items={weeklyItems}
              onCardClick={onNavigateToWeeklyCategory}
              type={COLLECTION_TYPE.WORKOUT}
              title={
                <SectionHeader>
                  <SectionTitle>Weekly Workouts</SectionTitle>
                </SectionHeader>
              }
            />
          </CollectionWrapper>
        )}

        {otherFamilies.map(family => (
          <React.Fragment key={family.id}>
            {family.workoutsCategories.length > 0 && (
              <SectionHeader>
                <SectionTitle>{family.title}</SectionTitle>
                {family.workoutsCategories.length > 3 && (
                  <ViewAllButton
                    onClick={() => onViewAllFamily(family)}
                    text={t("actions.viewAll")}
                    iconRight={<I24ArrowRightSmall />}
                    height={40}
                    width={104}
                    inverted
                  />
                )}
              </SectionHeader>
            )}
            <GridCollection
              items={family.workoutsCategories.slice(0, 3)}
              onClick={item => onNavigateToCategory(item, family.id, family.title)}
              needMore={needMore}
              onLoadMore={onLoadMore}
              withInfinityScroll
            />
          </React.Fragment>
        ))}

        {weeklyItems.length === 0 && (
          <CollectionWrapper>
            <Placeholder
              imageUrl={Placeholders.Workout}
              text={t("messages.no-workouts-studio")}
              inline={false}
            />
          </CollectionWrapper>
        )}
      </Wrapper>
    </Page>
  );
});

export default withTranslation()(WorkoutMain);
